/**
 * Models trained on code-interpreter output sometimes write `sandbox:/chat/attachments/{uuid}/file`.
 * Streamdown blocks the `sandbox:` scheme, so rewrite those to the plain backend path.
 */
export function stripSandboxUrls(text: string): string {
  const marker = "](sandbox:/";
  let out = "";
  let from = 0;
  let at = text.indexOf(marker, from);
  while (at !== -1) {
    const close = text.indexOf(")", at + marker.length);
    if (close === -1) break;
    const path = text.slice(at + marker.length, close).replace(/^\//, "");
    out += `${text.slice(from, at)}](/${path})`;
    from = close + 1;
    at = text.indexOf(marker, from);
  }
  return out + text.slice(from);
}

/**
 * The backend can reference the same image twice (a temporary storage URL and the stable
 * `/chat/attachments/` one). Keep the first image per name, extension ignored.
 */
export function dedupeImages(text: string): string {
  const seen = new Set<string>();
  return text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt: string, url: string) => {
    const name = alt.trim() || safeDecode(url.split("/").pop()?.split("?")[0] ?? "");
    if (!name) return match;
    const key = name.replace(/\.\w+$/, "").toLowerCase();
    if (seen.has(key)) return "";
    seen.add(key);
    return match;
  });
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Assistant markdown ready to render. Image dedupe waits for the finished reply. */
export function prepareMarkdown(text: string, streaming: boolean): string {
  const clean = stripSandboxUrls(text);
  return streaming ? clean : dedupeImages(clean);
}
